// «Mi banco» del técnico — Taller → Operación → Mi banco (954).
// Pantalla móvil-first: el técnico la abre desde el celular en el banco de
// trabajo. Botones grandes, buen contraste en modo oscuro. Sin ventanas
// modales: todo son vistas que reemplazan la pantalla (PanelIntegrado /
// useDialogoEnLinea), con «Volver» para regresar a la lista.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  AlertTriangle, ArrowLeft, Camera, CheckCircle2, ChevronRight, Gamepad2, Pause, Play, QrCode,
  ShieldCheck, Timer, Wrench, Zap,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { EscanerQR } from '@/components/ui/EscanerQR';
import { getErrorMessage } from '@/lib/api/client';
import { cn } from '@/lib/utils';
import { comprimirFoto } from '@/features/hotel/hkOffline';
import {
  tallerRpc, QK, ESTADOS, fmtFecha, useDialogoEnLinea,
  type EstadoOrden, type OrdenDetalle,
} from './shared';
import { AvisoDictado, BenchForm, CertificadoBench, type Pruebas } from './bench';

// ── Contrato de taller.mi_banco() ───────────────────────────────────────────
interface MiBancoRepuesto { id: string; descripcion: string; cantidad: number; original: boolean; serial: string | null; en_garantia: boolean }
interface MiBancoOrden {
  id: string; codigo: string; estado: EstadoOrden; prioridad: 'normal' | 'urgente'; falla: string; diagnostico: string | null;
  trabajo_realizado: string | null; pruebas: Record<string, unknown>; fecha_prometida: string | null; asignada: boolean; reingreso: boolean;
  garantia_dictamen: 'cubre' | 'no_cubre' | null; equipo: string; tipo: string; serial: string | null;
  minutos_reales: number; minutos_estandar: number; bancos: string[]; repuestos: MiBancoRepuesto[];
}
interface MiBancoResp {
  tecnico: { id: string; nombre: string; bancos: string[] } | null;
  error: string | null;
  puede_elegir: boolean;
  tecnicos: { id: string; nombre: string }[] | null;
  activo: { orden_id: string; inicio: string } | null;
  ordenes: MiBancoOrden[];
}

// Vista unificada de la orden abierta: de la cola propia o traída por QR fuera de ella.
interface VistaOrden {
  id: string; codigo: string; estado: EstadoOrden; prioridad: string; falla: string; diagnostico: string | null;
  trabajo_realizado: string | null; pruebas: Record<string, unknown>; equipo: string; tipo: string; serial: string | null;
  reingreso: boolean; garantia_dictamen: 'cubre' | 'no_cubre' | null; bancos: string[];
  minutos_reales: number; minutos_estandar: number; repuestos: MiBancoRepuesto[]; fueraDeLaCola: boolean;
}

const deOrden = (o: MiBancoOrden): VistaOrden => ({
  id: o.id, codigo: o.codigo, estado: o.estado, prioridad: o.prioridad, falla: o.falla, diagnostico: o.diagnostico,
  trabajo_realizado: o.trabajo_realizado, pruebas: o.pruebas ?? {}, equipo: o.equipo || o.tipo, tipo: o.tipo, serial: o.serial,
  reingreso: o.reingreso, garantia_dictamen: o.garantia_dictamen, bancos: o.bancos, minutos_reales: o.minutos_reales,
  minutos_estandar: o.minutos_estandar, repuestos: o.repuestos, fueraDeLaCola: false,
});
const deDetalle = (d: OrdenDetalle): VistaOrden => {
  const aprobadas = d.presupuestos.flatMap((p) => p.lineas.filter((l) => l.estado === 'aprobada'));
  return {
    id: d.id, codigo: d.codigo, estado: d.estado, prioridad: d.prioridad, falla: d.falla_reportada, diagnostico: d.diagnostico,
    trabajo_realizado: d.trabajo_realizado, pruebas: d.pruebas ?? {}, equipo: [d.equipo.marca, d.equipo.modelo].filter(Boolean).join(' ') || d.equipo.tipo, tipo: d.equipo.tipo,
    serial: d.equipo.serial, reingreso: d.reingreso_de != null, garantia_dictamen: d.garantia_dictamen, bancos: [],
    minutos_reales: 0, minutos_estandar: 0,
    repuestos: aprobadas.filter((l) => l.tipo === 'repuesto').map((l) => ({
      id: l.id, descripcion: l.descripcion, cantidad: Number(l.cantidad), original: l.original, serial: l.serial_repuesto, en_garantia: l.en_garantia,
    })),
    fueraDeLaCola: true,
  };
};

const transcurrido = (desde: string, hasta: number) => {
  const s = Math.max(0, Math.floor((hasta - new Date(desde).getTime()) / 1000));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), ss = s % 60;
  return `${h > 0 ? `${h}:` : ''}${String(m).padStart(h > 0 ? 2 : 1, '0')}:${String(ss).padStart(2, '0')}`;
};

export function MiBanco() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  // técnico elegido por un administrador (se recuerda en la pestaña)
  const [tecnicoId, setTecnicoIdRaw] = useState<string | null>(() => {
    try { return sessionStorage.getItem('taller_mi_banco_tecnico') || null; } catch { return null; }
  });
  const setTecnicoId = (id: string | null) => {
    setTecnicoIdRaw(id);
    setOrdenId(null); setOrdenExterna(null);
    try { if (id) sessionStorage.setItem('taller_mi_banco_tecnico', id); else sessionStorage.removeItem('taller_mi_banco_tecnico'); } catch { /* sin almacenamiento */ }
  };
  const [ordenId, setOrdenId] = useState<string | null>(null);
  const [ordenExterna, setOrdenExterna] = useState<OrdenDetalle | null>(null);
  const [escaneando, setEscaneando] = useState(false);

  const { data, isLoading } = useQuery<MiBancoResp>({
    queryKey: [QK, 'mi-banco', tecnicoId],
    queryFn: () => tallerRpc<MiBancoResp>('mi_banco', { _tecnico: tecnicoId }),
    refetchInterval: 20000,
  });

  const ordenesPorId = useMemo(() => new Map((data?.ordenes ?? []).map((o) => [o.id, o])), [data?.ordenes]);
  const vistaOrden: VistaOrden | null = useMemo(() => {
    if (!ordenId) return null;
    const propia = ordenesPorId.get(ordenId);
    if (propia) return deOrden(propia);
    if (ordenExterna?.id === ordenId) return deDetalle(ordenExterna);
    return null;
  }, [ordenId, ordenesPorId, ordenExterna]);

  const inv = () => void qc.invalidateQueries({ queryKey: [QK] });

  const iniciar = useMutation({
    mutationFn: (orden: string) => tallerRpc('trabajo_iniciar', { _orden: orden, _tecnico: tecnicoId }),
    onSuccess: () => { toast.success('Cronómetro iniciado'); inv(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const pausar = useMutation({
    mutationFn: (p: { orden: string; nota: string | null }) => tallerRpc('trabajo_pausar', { _orden: p.orden, _nota: p.nota, _tecnico: tecnicoId }),
    onSuccess: () => { toast.success('Cronómetro pausado'); inv(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const handlePausar = async (orden: string) => {
    const v = await dlg.pedir('Pausar el trabajo', [{ key: 'nota', label: 'Nota (opcional)', placeholder: 'p. ej. salí a almorzar', minimo: 0 }], { aceptar: 'Pausar' });
    if (v === null) return;
    pausar.mutate({ orden, nota: v.nota || null });
  };

  const abrirOrden = (id: string) => { setOrdenExterna(null); setOrdenId(id); };

  const handleEscaneo = async (texto: string) => {
    const m = texto.match(/([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})/);
    const token = m ? m[1] : texto.trim();
    setEscaneando(false);
    try {
      const r = await tallerRpc<{ id: string; codigo: string } | null>('orden_por_token', { _token: token });
      if (!r) { toast.error('El QR no corresponde a una orden de este taller'); return; }
      if (ordenesPorId.has(r.id)) { abrirOrden(r.id); return; }
      const det = await tallerRpc<OrdenDetalle>('orden_obtener', { _id: r.id });
      setOrdenExterna(det);
      setOrdenId(r.id);
      toast.info('Esta orden no está en tu cola: se abrió igual');
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  if (isLoading || !data) {
    return <div className="flex min-h-[60vh] items-center justify-center text-sm text-muted-foreground">Cargando tu banco…</div>;
  }

  // Sin técnico vinculado: elegir uno (solo nivel 4+) o mensaje de error.
  if (data.error) {
    if (!data.puede_elegir) {
      return (
        <div className="mx-auto max-w-md space-y-3 p-6 text-center">
          <AlertTriangle className="mx-auto h-10 w-10 text-amber-500" />
          <p className="text-base font-semibold">No se pudo abrir tu banco</p>
          <p className="text-sm text-muted-foreground">{data.error}</p>
        </div>
      );
    }
    return (
      <div className="mx-auto max-w-md space-y-3 p-4">
        <h1 className="flex items-center gap-2 text-lg font-bold"><Wrench className="h-5 w-5" /> Elegir técnico</h1>
        <p className="text-sm text-muted-foreground">{data.error}</p>
        <div className="space-y-2">
          {(data.tecnicos ?? []).map((t) => (
            <button key={t.id} type="button" onClick={() => setTecnicoId(t.id)}
              className="flex w-full items-center justify-between rounded-xl border bg-card px-4 py-4 text-left text-base font-medium shadow-sm active:scale-[0.99]">
              {t.nombre} <ChevronRight className="h-5 w-5 text-muted-foreground" />
            </button>
          ))}
          {(data.tecnicos ?? []).length === 0 && <p className="text-sm text-muted-foreground">No hay técnicos activos.</p>}
        </div>
      </div>
    );
  }

  if (escaneando) {
    return (
      <div className="p-3">
        <EscanerQR integrado titulo="Escanear etiqueta del equipo" onCerrar={() => setEscaneando(false)} onLeer={(t) => void handleEscaneo(t)} />
      </div>
    );
  }

  if (vistaOrden) {
    return (
      <DetalleOrden
        o={vistaOrden}
        activo={data.activo}
        tecnicoId={tecnicoId}
        onVolver={() => { setOrdenId(null); setOrdenExterna(null); }}
        onIniciar={() => iniciar.mutate(vistaOrden.id)}
        onPausar={() => void handlePausar(vistaOrden.id)}
        cargandoIniciar={iniciar.isPending}
        cargandoPausar={pausar.isPending}
      />
    );
  }

  return (
    <div className="mx-auto max-w-lg space-y-3 p-3 pb-10">
      <div className="flex items-center justify-between gap-2 px-1">
        <h1 className="flex items-center gap-2 text-lg font-bold"><Gamepad2 className="h-5 w-5" /> Mi banco{data.tecnico && !data.puede_elegir ? ` · ${data.tecnico.nombre}` : ''}</h1>
        <Button size="sm" variant="outline" onClick={() => setEscaneando(true)}><QrCode className="mr-1 h-4 w-4" /> Escanear</Button>
      </div>
      {data.puede_elegir && (data.tecnicos ?? []).length > 0 && (
        <label className="flex items-center gap-2 px-1 text-sm">
          <span className="shrink-0 text-muted-foreground">Técnico:</span>
          <select className="h-11 w-full rounded-lg border bg-background px-3 text-base font-medium" value={data.tecnico?.id ?? ''}
            onChange={(e) => setTecnicoId(e.target.value || null)}>
            {!data.tecnico && <option value="">— Elegir —</option>}
            {(data.tecnicos ?? []).map((t) => <option key={t.id} value={t.id}>{t.nombre}</option>)}
          </select>
        </label>
      )}

      {data.activo && (
        <CronometroBanner inicio={data.activo.inicio} orden={ordenesPorId.get(data.activo.orden_id)}
          onAbrir={() => abrirOrden(data.activo!.orden_id)}
          onPausar={() => void handlePausar(data.activo!.orden_id)} pausando={pausar.isPending} />
      )}

      {data.tecnico?.bancos && data.tecnico.bancos.length > 0 && (
        <p className="px-1 text-xs text-muted-foreground">Certificado en: {data.tecnico.bancos.join(', ')}</p>
      )}

      <Listado ordenes={data.ordenes} onAbrir={abrirOrden} />
    </div>
  );
}

// ── Banner del cronómetro activo (arriba de la lista) ───────────────────────
function CronometroBanner({ inicio, orden, onAbrir, onPausar, pausando }: {
  inicio: string; orden: MiBancoOrden | undefined; onAbrir: () => void; onPausar: () => void; pausando: boolean;
}) {
  const [, forzar] = useState(0);
  useEffect(() => { const id = setInterval(() => forzar((n) => n + 1), 1000); return () => clearInterval(id); }, []);
  return (
    <Card className="border-emerald-400 bg-emerald-50 dark:border-emerald-700 dark:bg-emerald-950/30">
      <CardContent className="flex items-center justify-between gap-3 p-4">
        <button type="button" onClick={onAbrir} className="min-w-0 flex-1 text-left">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
            <Timer className="h-4 w-4 animate-pulse" /> Trabajando
          </div>
          <div className="font-mono text-2xl font-black tabular-nums text-emerald-900 dark:text-emerald-100">{transcurrido(inicio, Date.now())}</div>
          {orden && <div className="truncate text-sm text-emerald-800 dark:text-emerald-200">{orden.codigo} · {orden.equipo || orden.tipo}</div>}
        </button>
        <Button size="lg" variant="outline" isLoading={pausando} onClick={onPausar} className="h-14 w-14 shrink-0 rounded-full border-2 border-emerald-500 p-0">
          <Pause className="h-6 w-6" />
        </Button>
      </CardContent>
    </Card>
  );
}

// ── Lista de tarjetas ────────────────────────────────────────────────────────
function Listado({ ordenes, onAbrir }: { ordenes: MiBancoOrden[]; onAbrir: (id: string) => void }) {
  const asignadas = ordenes.filter((o) => o.asignada);
  const libres = ordenes.filter((o) => !o.asignada);
  return (
    <div className="space-y-4">
      <Grupo titulo="Mis órdenes" ordenes={asignadas} onAbrir={onAbrir} vacio="No tienes órdenes asignadas." />
      <Grupo titulo="Sin asignar · tomar" ordenes={libres} onAbrir={onAbrir} vacio="No hay órdenes libres en este momento." />
    </div>
  );
}

function Grupo({ titulo, ordenes, onAbrir, vacio }: { titulo: string; ordenes: MiBancoOrden[]; onAbrir: (id: string) => void; vacio: string }) {
  return (
    <div className="space-y-2">
      <h2 className="px-1 text-sm font-semibold text-muted-foreground">{titulo} ({ordenes.length})</h2>
      {ordenes.length === 0 && <p className="px-1 text-sm text-muted-foreground">{vacio}</p>}
      {ordenes.map((o) => <TarjetaOrden key={o.id} o={o} onClick={() => onAbrir(o.id)} />)}
    </div>
  );
}

function BarraMinutos({ reales, estandar }: { reales: number; estandar: number }) {
  if (estandar <= 0) return null;
  const pct = Math.min(100, Math.round((reales / estandar) * 100));
  const pasado = reales > estandar;
  return (
    <div className="mt-1.5">
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div className={cn('h-full rounded-full', pasado ? 'bg-rose-500' : 'bg-emerald-500')} style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-0.5 text-[11px] text-muted-foreground">{reales} / {estandar} min estándar</div>
    </div>
  );
}

function TarjetaOrden({ o, onClick }: { o: MiBancoOrden; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick}
      className="block w-full rounded-xl border bg-card p-4 text-left shadow-sm transition-all active:scale-[0.99] active:shadow-inner">
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-base font-bold">{o.codigo}</span>
        <div className="flex items-center gap-1">
          {o.prioridad === 'urgente' && <span className="inline-flex items-center gap-0.5 rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold text-rose-800 dark:bg-rose-900/40 dark:text-rose-300"><Zap className="h-3 w-3" /> URGENTE</span>}
          {o.reingreso && <span className="rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold text-violet-800 dark:bg-violet-900/40 dark:text-violet-300">REINGRESO</span>}
        </div>
      </div>
      <div className="mt-1 truncate text-base font-medium">{o.equipo || o.tipo}</div>
      {o.serial && <div className="font-mono text-xs text-muted-foreground">{o.serial}</div>}
      <div className="mt-1 truncate text-sm text-muted-foreground" title={o.falla}>{o.falla}</div>
      <div className="mt-2 flex items-center justify-between">
        <span className={`inline-block rounded-full px-2 py-0.5 text-xs ${ESTADOS[o.estado].cls}`}>{ESTADOS[o.estado].label}</span>
        {o.fecha_prometida && <span className="text-xs text-muted-foreground">Prometida {fmtFecha(o.fecha_prometida)}</span>}
      </div>
      <BarraMinutos reales={o.minutos_reales} estandar={o.minutos_estandar} />
      {o.bancos.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {o.bancos.map((b) => <span key={b} className="rounded-full bg-muted px-2 py-0.5 text-[11px]">{b}</span>)}
        </div>
      )}
    </button>
  );
}

// ── Detalle de la orden abierta ──────────────────────────────────────────────
const TRANSICIONES: Partial<Record<EstadoOrden, { label: string; to: EstadoOrden; principal?: boolean }[]>> = {
  recibido: [{ label: 'Pasar a diagnóstico', to: 'diagnostico', principal: true }],
  aprobado: [{ label: 'Iniciar reparación', to: 'reparacion', principal: true }],
  reparacion: [{ label: 'Esperando repuesto', to: 'esperando_repuesto' }, { label: 'Pasar a pruebas', to: 'pruebas', principal: true }],
  esperando_repuesto: [{ label: 'Volver a reparación', to: 'reparacion', principal: true }],
  pruebas: [{ label: 'Volver a reparación', to: 'reparacion' }, { label: 'Marcar listo', to: 'listo', principal: true }],
};


function DetalleOrden({ o, activo, tecnicoId, onVolver, onIniciar, onPausar, cargandoIniciar, cargandoPausar }: {
  o: VistaOrden; activo: { orden_id: string; inicio: string } | null; tecnicoId: string | null; onVolver: () => void;
  onIniciar: () => void; onPausar: () => void; cargandoIniciar: boolean; cargandoPausar: boolean;
}) {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const inv = () => void qc.invalidateQueries({ queryKey: [QK] });
  const estaActiva = activo?.orden_id === o.id;

  const [diagnostico, setDiagnostico] = useState(o.diagnostico ?? '');
  const [trabajo, setTrabajo] = useState(o.trabajo_realizado ?? '');
  const [pruebas, setPruebas] = useState<Pruebas>(() => ({ ...(o.pruebas ?? {}) }));
  // si la orden cambia (abrir otra), recargar los campos
  const idRef = useRef(o.id);
  useEffect(() => {
    if (idRef.current === o.id) return;
    idRef.current = o.id;
    setDiagnostico(o.diagnostico ?? ''); setTrabajo(o.trabajo_realizado ?? '');
    setPruebas({ ...(o.pruebas ?? {}) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [o.id]);

  const estado = useMutation({
    mutationFn: (e: EstadoOrden) => tallerRpc('orden_estado', { _id: o.id, _estado: e, _nota: null, _publica: true }),
    onSuccess: () => { toast.success('Estado actualizado'); inv(); },
    onError: (e2) => toast.error(getErrorMessage(e2)),
  });
  const guardarTrabajo = useMutation({
    mutationFn: () => tallerRpc('orden_actualizar', { _id: o.id, _d: { diagnostico: diagnostico.trim() || null, trabajo_realizado: trabajo.trim() || null, pruebas } }),
    onSuccess: () => { toast.success('Guardado'); inv(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const repuesto = useMutation({
    mutationFn: (x: { linea: string; original: boolean; serial: string | null }) => tallerRpc('linea_repuesto', { _linea: x.linea, _original: x.original, _serial: x.serial }),
    onSuccess: () => { toast.success('Repuesto actualizado'); inv(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const [subiendo, setSubiendo] = useState(false);
  const subirFoto = async (files: FileList | null) => {
    if (!files?.length) return;
    setSubiendo(true);
    try {
      const datos = await comprimirFoto(files[0]);
      await tallerRpc('foto_subir', { _orden: o.id, _etapa: o.estado, _datos: datos, _mime: 'image/jpeg', _descripcion: null, _publica: true });
      toast.success('Foto agregada');
      inv();
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setSubiendo(false); }
  };
  const [nota, setNota] = useState('');
  const [notaPublica, setNotaPublica] = useState(false);
  const enviarNota = useMutation({
    mutationFn: () => tallerRpc('orden_actualizar', { _id: o.id, _d: { nota: nota.trim(), publica: notaPublica } }),
    onSuccess: () => { toast.success('Nota registrada'); setNota(''); inv(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const transiciones = TRANSICIONES[o.estado] ?? [];
  void tecnicoId;

  return (
    <div className="mx-auto max-w-lg space-y-3 p-3 pb-10">
      {dlg.ui}
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={onVolver}><ArrowLeft className="mr-1 h-4 w-4" /> Volver</Button>
        <span className="font-mono text-lg font-bold">{o.codigo}</span>
        <span className={`rounded-full px-2 py-0.5 text-xs ${ESTADOS[o.estado].cls}`}>{ESTADOS[o.estado].label}</span>
      </div>

      {o.fueraDeLaCola && (
        <div className="flex items-center gap-2 rounded-md border border-sky-300 bg-sky-50 px-3 py-2 text-sm text-sky-900 dark:border-sky-800 dark:bg-sky-950/30 dark:text-sky-200">
          <AlertTriangle className="h-4 w-4 shrink-0" /> Esta orden no está en tu cola todavía. Al iniciar el trabajo quedará asignada a ti.
        </div>
      )}
      {o.reingreso && (
        <div className="flex items-center gap-2 rounded-md border border-violet-300 bg-violet-50 px-3 py-2 text-sm text-violet-900 dark:border-violet-800 dark:bg-violet-950/30 dark:text-violet-200">
          <AlertTriangle className="h-4 w-4 shrink-0" /> Reingreso{o.garantia_dictamen ? ` · garantía: ${o.garantia_dictamen === 'cubre' ? 'cubre' : 'no cubre'}` : ' · sin dictamen todavía'}
        </div>
      )}

      <Card>
        <CardContent className="space-y-1 p-4">
          <div className="text-base font-semibold">{o.equipo}</div>
          {o.serial && <div className="font-mono text-xs text-muted-foreground">{o.serial}</div>}
          <div className="text-sm text-muted-foreground">{o.falla}</div>
          {!o.fueraDeLaCola && <BarraMinutos reales={o.minutos_reales} estandar={o.minutos_estandar} />}
        </CardContent>
      </Card>

      {/* Cronómetro grande */}
      <div className="flex gap-2">
        {estaActiva ? (
          <Button size="lg" variant="destructive" isLoading={cargandoPausar} onClick={onPausar} className="h-16 flex-1 text-lg font-bold">
            <Pause className="mr-2 h-6 w-6" /> Pausar
          </Button>
        ) : (
          <Button size="lg" isLoading={cargandoIniciar} onClick={onIniciar} className="h-16 flex-1 text-lg font-bold">
            <Play className="mr-2 h-6 w-6" /> Iniciar
          </Button>
        )}
      </div>

      {o.estado === 'diagnostico' && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
          Arme el presupuesto en Órdenes para avanzar esta orden.
        </div>
      )}

      {transiciones.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {transiciones.map((t) => (
            <Button key={t.to} size="lg" variant={t.principal ? 'default' : 'outline'} isLoading={estado.isPending}
              className="h-14 flex-1 text-base font-semibold" onClick={() => estado.mutate(t.to)}>
              {t.label}
            </Button>
          ))}
        </div>
      )}

      {/* Diagnóstico, trabajo y pruebas de bench */}
      <Card>
        <CardContent className="space-y-3 p-4">
          <h3 className="text-sm font-semibold">Diagnóstico y trabajo</h3>
          <AvisoDictado />
          <Campo label="Diagnóstico">
            <textarea className="h-20 w-full rounded-md border border-input bg-background p-2 text-sm" value={diagnostico} onChange={(e) => setDiagnostico(e.target.value)} />
          </Campo>
          <Campo label="Trabajo realizado">
            <textarea className="h-20 w-full rounded-md border border-input bg-background p-2 text-sm" value={trabajo} onChange={(e) => setTrabajo(e.target.value)} />
          </Campo>
          <BenchForm tipo={o.tipo} valor={pruebas} onChange={setPruebas} grande />
          <Button isLoading={guardarTrabajo.isPending} onClick={() => guardarTrabajo.mutate()}><CheckCircle2 className="mr-1 h-4 w-4" /> Guardar</Button>
          <CertificadoBench ordenId={o.id} estadoOrden={o.estado}
            guardarAntes={() => tallerRpc('orden_actualizar', { _id: o.id, _d: { diagnostico: diagnostico.trim() || null, trabajo_realizado: trabajo.trim() || null, pruebas } })} />
        </CardContent>
      </Card>

      {/* Repuestos instalados */}
      {o.repuestos.length > 0 && (
        <Card>
          <CardContent className="space-y-3 p-4">
            <h3 className="text-sm font-semibold">Repuestos instalados</h3>
            {o.repuestos.map((r) => (
              <div key={r.id} className="space-y-2 rounded-md border p-2.5">
                <div className="flex items-center justify-between gap-2 text-sm">
                  <span className="truncate font-medium">{r.cantidad}× {r.descripcion}</span>
                  {r.en_garantia && <span className="shrink-0 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">GARANTÍA</span>}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button type="button" onClick={() => repuesto.mutate({ linea: r.id, original: true, serial: r.serial })}
                    className={cn('rounded-full border px-3 py-1 text-xs', r.original ? 'border-primary bg-primary text-primary-foreground' : 'border-input text-muted-foreground')}>
                    Original
                  </button>
                  <button type="button" onClick={() => repuesto.mutate({ linea: r.id, original: false, serial: r.serial })}
                    className={cn('rounded-full border px-3 py-1 text-xs', !r.original ? 'border-primary bg-primary text-primary-foreground' : 'border-input text-muted-foreground')}>
                    Alternativo
                  </button>
                  <input placeholder="N.º de serie" defaultValue={r.serial ?? ''}
                    onBlur={(e) => { if (e.target.value !== (r.serial ?? '')) repuesto.mutate({ linea: r.id, original: r.original, serial: e.target.value || null }); }}
                    className="h-8 flex-1 min-w-[8rem] rounded-md border border-input bg-background px-2 text-xs" />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Foto */}
      <Card>
        <CardContent className="space-y-2 p-4">
          <h3 className="text-sm font-semibold">Foto</h3>
          <label className={cn('flex h-14 w-full cursor-pointer items-center justify-center gap-2 rounded-md bg-primary text-base font-semibold text-primary-foreground', subiendo && 'opacity-60')}>
            <Camera className="h-5 w-5" /> {subiendo ? 'Subiendo…' : 'Tomar foto'}
            <input type="file" accept="image/*" capture="environment" className="hidden" disabled={subiendo} onChange={(e) => void subirFoto(e.target.files)} />
          </label>
        </CardContent>
      </Card>

      {/* Nota rápida */}
      <Card>
        <CardContent className="space-y-2 p-4">
          <h3 className="text-sm font-semibold">Nota rápida</h3>
          <textarea className="h-16 w-full rounded-md border border-input bg-background p-2 text-sm" placeholder="p. ej. se cambió pasta térmica…" value={nota} onChange={(e) => setNota(e.target.value)} />
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={notaPublica} onChange={(e) => setNotaPublica(e.target.checked)} /> Visible para el cliente</label>
          <Button variant="outline" disabled={!nota.trim()} isLoading={enviarNota.isPending} onClick={() => enviarNota.mutate()}>
            <ShieldCheck className="mr-1 h-4 w-4" /> Guardar nota
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function Campo({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="block space-y-1"><span className="text-xs font-medium text-muted-foreground">{label}</span>{children}</label>;
}
